import { Config } from '@/app/constants';
import type { UserProfile } from '@/common/model/types';
import { getPlatform } from '@/lib/platform';
import { HttpError } from '@/services/http';
import {
  clearNativeTokens,
  getNativeRefreshToken,
  isNativeTokenResponse,
  saveNativeTokens,
  usesBearerAuth,
  type NativeTokenResponse,
} from '@/services/nativeSession';

const API_URL = Config.api.baseUrl;

export interface AuthLoginResponse {
  /** Web only; the native app keeps its token in `nativeSession`. */
  access_token?: string;
  user: {
    id: string;
    email: string;
    name: string | null;
    role: string;
    emailVerified: boolean;
    club?: { id: string; name: string; city: string | null } | null;
  };
}

/**
 * Message d'erreur renvoyé par le serveur, ou chaîne vide s'il n'y en a pas
 * d'exploitable.
 *
 * `class-validator` remonte un **tableau** de messages sur une 400 : sans ce
 * cas, une erreur de validation était perdue au profit du message générique.
 */
function extractServerMessage(data: unknown): string {
  if (typeof data !== 'object' || data === null || !('message' in data)) return '';
  const message = (data as { message: unknown }).message;
  if (typeof message === 'string') return message.trim();
  if (Array.isArray(message)) {
    return message.filter((m): m is string => typeof m === 'string').join(' ').trim();
  }
  return '';
}

/**
 * A refused request, carrying the server's message: only these reach the screen as they are
 * (any other error shows the screen's translated text, VOL-343).
 */
function serverError(response: Response, data: unknown): HttpError {
  return new HttpError(response.status, extractServerMessage(data), data);
}

/** The device could not keep the session (Keystore, secure storage plugin). */
export class DeviceSessionError extends Error {
  constructor(cause: unknown) {
    super('Device session storage failed', { cause });
    this.name = 'DeviceSessionError';
  }
}

const DEVICE_NAME_TIMEOUT_MS = 1_000;

/** Only a label in the device list: it never holds up nor fails the sign-in. */
async function currentDeviceName(): Promise<string | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), DEVICE_NAME_TIMEOUT_MS);
  });
  try {
    return await Promise.race([getPlatform().device.name().catch(() => null), timeout]);
  } finally {
    clearTimeout(timer);
  }
}

async function nativeLogin(email: string, password: string): Promise<AuthLoginResponse> {
  const deviceName = await currentDeviceName();
  const response = await fetch(`${API_URL}/auth/native/login`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(deviceName ? { email, password, deviceName } : { email, password }),
  });
  const data: unknown = await response.json().catch(() => ({}));
  if (!response.ok) throw serverError(response, data);
  // Not an HttpError: the login screen shows its own translated error.
  if (!isNativeTokenResponse(data) || !('user' in data)) throw new Error('Malformed native login response');
  const { accessToken, refreshToken, user } = data as NativeTokenResponse<AuthLoginResponse['user']>;
  try {
    await saveNativeTokens({ accessToken, refreshToken });
  } catch (error) {
    // Its raw message ("SecureStoragePlugin is not implemented on android") once reached the screen.
    throw new DeviceSessionError(error);
  }
  return { user };
}

/** Signs the device out on the server; the local tokens go whatever the answer. */
async function nativeLogout(): Promise<void> {
  try {
    const refreshToken = await getNativeRefreshToken();
    if (refreshToken) {
      const response = await fetch(`${API_URL}/auth/native/logout`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) throw new Error('Logout failed');
    }
  } finally {
    await clearNativeTokens();
  }
}

export const AuthService = {
  async login(email: string, password: string): Promise<AuthLoginResponse> {
    if (usesBearerAuth()) return nativeLogin(email, password);
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data: unknown = await response.json().catch(() => ({}));
    if (!response.ok) {
      // Message vide plutôt qu'un texte français codé en dur : `LoginScreen`
      // retombe alors sur sa propre chaîne traduite. Le service n'a pas accès
      // à `t()`, c'est donc à l'écran de choisir le libellé par défaut.
      throw serverError(response, data);
    }
    return data as AuthLoginResponse;
  },

  async register(
    email: string,
    password: string,
    name?: string,
    invitationCode?: string,
    role?: string,
    acceptedCguVersion?: string,
  ) {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, invitationCode, role, acceptedCguVersion }),
    });
    if (!response.ok) {
      // HttpError keeps the body's `code` (CGU_OUTDATED), which the screen translates.
      throw serverError(response, await response.json().catch(() => ({})));
    }
    return response.json() as Promise<{
      message: string;
      user: {
        id: string;
        email: string;
        name: string | null;
        role: string;
        emailVerified: boolean;
        club?: { id: string; name: string; city: string | null } | null;
      };
    }>;
  },

  async confirmEmailFromToken(token: string): Promise<{
    message: string;
    user?: {
      id: string;
      email: string;
      name: string | null;
      role: string;
      emailVerified: boolean;
      club?: { id: string; name: string; city: string | null } | null;
    };
    access_token?: string;
  }> {
    const response = await fetch(`${API_URL}/auth/confirm?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const data: unknown = await response.json().catch(() => ({}));
    if (!response.ok) {
      // Même logique que `login` : sans message serveur, `ConfirmEmailScreen`
      // utilise son `confirm_email.error_generic` traduit, qui était jusqu'ici
      // du code mort puisque ce fallback français le devançait toujours.
      throw new Error(extractServerMessage(data));
    }
    if (usesBearerAuth()) {
      // The confirmation opens a cookie session, which the app does not use:
      // without `user` the screen says the address is confirmed and sends the coach to sign in.
      const { message } = data as { message: string };
      return { message };
    }
    return data as {
      message: string;
      user?: {
        id: string;
        email: string;
        name: string | null;
        role: string;
        emailVerified: boolean;
        club?: { id: string; name: string; city: string | null } | null;
      };
      access_token?: string;
    };
  },

  async logout() {
    if (usesBearerAuth()) return nativeLogout();
    const response = await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Logout failed');
    return response.json();
  },

  async forgotPassword(email: string) {
    const response = await fetch(`${API_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) throw serverError(response, await response.json().catch(() => ({})));
    return response.json();
  },

  /**
   * Renvoie l'email de confirmation d'un compte non encore vérifié.
   *
   * Le serveur répond la même chose que le compte existe, soit déjà confirmé ou
   * n'existe pas : l'appelant ne peut donc rien en déduire, et ne doit rien
   * afficher d'autre que le message renvoyé.
   */
  async resendConfirmation(email: string): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/auth/resend-confirmation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) throw serverError(response, await response.json().catch(() => ({})));
    return response.json();
  },

  async resetPassword(token: string, newPassword: string) {
    const response = await fetch(`${API_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
    if (!response.ok) throw serverError(response, await response.json().catch(() => ({})));
    return response.json();
  },

  async getProfile(): Promise<UserProfile> {
    const { HttpService } = await import('./http.service');
    return HttpService.get<UserProfile>('/users/profile');
  },

  async updateProfile(data: Partial<UserProfile>): Promise<UserProfile> {
    const { HttpService } = await import('./http.service');
    return HttpService.put<UserProfile>('/users/profile', data);
  },

  async createInvitation(role: string) {
    const { HttpService } = await import('./http.service');
    return HttpService.post('/users/invite', { role });
  },

  async validateInvitationCode(code: string) {
    const response = await fetch(`${API_URL}/auth/validate-invitation?code=${encodeURIComponent(code)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      // Keeps the body: its `code` tells an expired link from an unknown one.
      throw serverError(response, await response.json().catch(() => null));
    }
    return response.json();
  },

  async applyInvitation(code: string): Promise<UserProfile> {
    const { HttpService } = await import('./http.service');
    return HttpService.post<UserProfile>('/auth/apply-invitation', { code });
  },
};

